// <biolae-quiz> engine — no markup or CSS lives here. Screens are <template>s in
// biolae-quiz-shell.liquid (Tailwind); copy is JSON. It clones templates and fills [data-q] slots.
// Flow: intro → [question | adaptive]… → contact → loading → result, then POSTs /api/submit
// to the quiz-worker (fires the Klaviyo "Completed Quiz" event).
(function () {
  class BiolaeQuiz extends HTMLElement {
    // --- DOM helpers ---

    cloneTemplate(templateName) {
      const template = document.getElementById("bq-tpl-" + templateName);
      return template && template.content.firstElementChild
        ? template.content.firstElementChild.cloneNode(true)
        : null;
    }

    findSlot(root, slotName) {
      return root.querySelector('[data-q="' + slotName + '"]');
    }

    // set text on a slot; remove the node when the value is empty
    setText(root, slotName, value) {
      const node = this.findSlot(root, slotName);
      if (!node) return null;
      if (value || value === 0) { node.textContent = value; return node; }
      node.remove(); return null;
    }

    // set inner HTML on a slot; remove the node when there's no markup
    setHtml(root, slotName, markup) {
      const node = this.findSlot(root, slotName);
      if (!node) return null;
      if (markup) { node.innerHTML = markup; return node; }
      node.remove(); return null;
    }

    // ask the Shopify CDN for a right-sized image
    optimizeSrc(url) {
      if (!url || url.indexOf("cdn.shopify.com") < 0 || /[?&]width=/.test(url)) return url;
      return url + (url.indexOf("?") > -1 ? "&" : "?") + "width=1200";
    }

    setImage(root, slotName, url, alt) {
      const image = this.findSlot(root, slotName);
      if (!image) return null;
      if (url) { image.setAttribute("src", this.optimizeSrc(url)); if (alt != null) image.setAttribute("alt", alt); return image; }
      image.remove(); return null;
    }

    // --- lifecycle ---

    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;
      this.workerUrl = (this.getAttribute("data-api") || "").replace(/\/$/, "");
      this.config = this.readConfig();
      this.live = this.readLive();

      // shell = close button + scroll + stage; multiple top-level nodes, so not cloneTemplate()
      const shellTemplate = document.getElementById("bq-tpl-shell");
      if (!shellTemplate) {
        console.error("[biolae-quiz] missing templates — render 'biolae-quiz-shell' once on the page");
        return;
      }
      this.append(shellTemplate.content.cloneNode(true));
      this.scrollArea = this.findSlot(this, "scroll");
      this.stage = this.findSlot(this, "inner");
      this.closeButton = this.findSlot(this, "close");
      if (this.closeButton) this.closeButton.addEventListener("click", () => this.close());

      this.setAttribute("role", "dialog");
      this.setAttribute("aria-modal", "true");
      this.setAttribute("aria-label", (this.config && this.config.intro && this.config.intro.heading) || "Quiz");
      // expose the quiz id for per-quiz template tweaks, e.g. [[data-quiz-id=creatone]_&]:mb-8
      if (this.config && this.config.quizId) this.setAttribute("data-quiz-id", this.config.quizId);
      this.steps = (this.config && this.config.steps) || [];
      this.reset();
      this.schedulePreload();
    }

    reset() {
      this.answers = {};
      this.firstName = "";
      this.email = "";
      this.phone = "";
      this.stepIndex = 0;
    }

    // warm the cache for every config image during idle time so each screen renders instantly
    schedulePreload() {
      const run = () => this.preloadImages();
      if ("requestIdleCallback" in window) requestIdleCallback(run, { timeout: 3000 });
      else setTimeout(run, 1500);
    }

    preloadImages() {
      const cfg = this.config;
      if (!cfg) return;
      const urls = new Set();
      const add = (url) => { if (url) urls.add(this.optimizeSrc(url)); };
      add(cfg.intro && cfg.intro.image);
      (cfg.steps || []).forEach((step) => {
        add(step.image);
        (step.variants || []).forEach((variant) => add(variant.image));
      });
      if (cfg.result) {
        add(cfg.result.image);
        if (cfg.result.cards && cfg.result.cards.promo) add(cfg.result.cards.promo.image);
        const plan = cfg.result.plan;
        if (plan) {
          if (plan.promo) add(plan.promo.image);
          Object.keys(plan.products || {}).forEach((id) => {
            add(plan.products[id].image);
            if (plan.products[id].solo) add(plan.products[id].solo.image); // solo screen pack shot
          });
        }
      }
      urls.forEach((url) => { const img = new Image(); img.src = url; });
    }

    readConfig() {
      return this.readJson(this.getAttribute("data-config"), "config");
    }

    // live Shopify data (Okendo rating, tags) emitted by biolae-quiz.liquid, keyed by handle
    readLive() {
      return this.readJson(this.getAttribute("data-live"), "live data") || {};
    }

    readJson(selector, label) {
      const scriptEl = selector ? document.querySelector(selector) : null;
      if (!scriptEl) return null;
      try { return JSON.parse(scriptEl.textContent); }
      catch (error) { console.error("[biolae-quiz] invalid " + label + " JSON:", error); return null; }
    }

    // --- open / close ---

    open() {
      if (this.hasAttribute("data-open")) return;
      if (this.parentElement !== document.body) document.body.appendChild(this); // avoid ancestor clipping
      this._previouslyFocused = document.activeElement;
      this.setAttribute("data-open", "");

      // scroll to top so header + announcement bar stay on screen, then lock scrolling (restored on close)
      this._savedScrollY = window.scrollY || window.pageYOffset || 0;
      window.scrollTo(0, 0);
      document.documentElement.classList.add("overflow-hidden");
      document.body.classList.add("overflow-hidden");

      // reset the header to its default state so both bars stay visible: undo header.js's
      // scrolled/sticky classes, and make a transparent header solid
      this.header = document.querySelector("header-487");
      if (this.header) {
        this.header.classList.remove("fixed", "top-0");
        this.header.style.transform = "";
        if (this.header.classList.contains("transparent-header")) {
          this.header.classList.remove("transparent-header");
          this._restoreTransparentHeader = true;
        }
      }

      // offset the popup below the announcement bar + header (re-measured on resize)
      this.syncTop();
      this._onResize = () => this.syncTop();
      window.addEventListener("resize", this._onResize);

      this.reset();
      if (!this.config) this.mount(this.cloneTemplate("error"));
      else this.renderIntro();
      this._onKeydown = (event) => { if (event.key === "Escape") this.close(); };
      document.addEventListener("keydown", this._onKeydown);
    }

    close() {
      this.removeAttribute("data-open");
      document.documentElement.classList.remove("overflow-hidden");
      document.body.classList.remove("overflow-hidden");
      this.style.top = "";
      if (this.closeButton) this.closeButton.style.top = "";
      window.removeEventListener("resize", this._onResize);
      // restore the transparent header, then the scroll position (lets header.js re-apply its own state)
      if (this._restoreTransparentHeader && this.header) {
        this.header.classList.add("transparent-header");
        this._restoreTransparentHeader = false;
      }
      if (this._savedScrollY != null) window.scrollTo(0, this._savedScrollY);
      document.removeEventListener("keydown", this._onKeydown);
      if (this._previouslyFocused && this._previouslyFocused.focus) this._previouslyFocused.focus();
    }

    // start the popup just below the announcement bar + header so both stay visible
    syncTop() {
      let bottom = 0;
      for (const selector of [".announcement-bar", "header-487"]) {
        document.querySelectorAll(selector).forEach((el) => {
          const rect = el.getBoundingClientRect();
          if (rect.height && rect.bottom > bottom) bottom = rect.bottom;
        });
      }
      const offset = Math.max(0, Math.round(bottom));
      this.style.top = offset + "px";
      if (this.closeButton) this.closeButton.style.top = offset + 18 + "px";
    }

    mount(screen) {
      this.stage.innerHTML = "";
      if (screen) this.stage.appendChild(screen);
      this.scrollArea.scrollTop = 0;
    }

    // replace the [data-q="progress"] placeholder with a filled progress bar
    fillProgressBar(root, percent) {
      const placeholder = this.findSlot(root, "progress");
      if (!placeholder) return;
      const progressBar = this.cloneTemplate("progress");
      const fill = this.findSlot(progressBar, "bar");
      if (fill) fill.style.width = percent + "%";
      placeholder.replaceWith(progressBar);
    }

    // contact screen is a full bar; step i of N → (i+1)/(N+1)
    progressPercent() {
      return this.steps.length ? ((this.stepIndex + 1) / (this.steps.length + 1)) * 100 : 0;
    }

    // --- intro ---

    renderIntro() {
      const intro = this.config.intro || {};
      const root = this.cloneTemplate("intro");
      this.setText(root, "heading", intro.heading);
      this.setText(root, "body", intro.body);
      const startButton = this.setText(root, "start", intro.button || "Let's start");
      if (startButton) startButton.addEventListener("click", () => this.goToStep(0));
      this.setHtml(root, "disclaimer", intro.disclaimer ? this.linkify(intro.disclaimer, intro.privacyUrl) : "");
      if (intro.image) this.setImage(root, "image", intro.image, intro.heading || "");
      else { const media = this.findSlot(root, "media"); if (media) media.remove(); }
      this.mount(root);
      this.focusFirst();
    }

    // --- step router (question / adaptive) ---

    goToStep(index) {
      if (index < 0) return this.renderIntro();
      if (index >= this.steps.length) return this.renderContact();
      this.stepIndex = index;
      const step = this.steps[index];
      if (step.type === "adaptive") this.renderAdaptive(step);
      else this.renderQuestion(step);
    }

    renderQuestion(step) {
      const isMulti = step.kind === "multi";
      const isInline = step.layout === "inline";
      const selectedValues = new Set([].concat(this.answers[step.id] || []));
      const root = this.cloneTemplate("question");
      this.fillProgressBar(root, this.progressPercent());
      this.setText(root, "title", step.title);
      this.setText(root, "subtitle", step.subtitle);

      const choicesGrid = this.findSlot(root, "choices");
      if (isInline) {
        choicesGrid.classList.remove("grid");
        choicesGrid.classList.add("flex", "flex-wrap", "justify-center", "max-w-none");
      } else if (step.columns === 3) {
        choicesGrid.classList.add("grid-cols-3", "max-w-[900px]");
      } else {
        choicesGrid.classList.add("grid-cols-2");
      }

      (step.choices || []).forEach((choice) => {
        const choiceEl = this.cloneTemplate(isMulti ? "choice-multi" : "choice-single");
        const input = this.findSlot(choiceEl, "input");
        input.name = step.id;
        input.value = choice.value;
        if (selectedValues.has(choice.value)) input.checked = true;
        input.addEventListener("change", () => this.updateContinueState(root, choicesGrid));
        this.setText(choiceEl, "label", choice.label);
        if (isInline) choiceEl.classList.add("w-full", "lg:max-w-[172px]");
        choicesGrid.appendChild(choiceEl);
      });

      const continueButton = this.setText(root, "continue", this.config.continueLabel || "Continue");
      if (continueButton) continueButton.addEventListener("click", () => this.commitQuestion(step, choicesGrid));
      this.wireBackButton(root);
      this.mount(root);
      this.updateContinueState(root, choicesGrid);
    }

    // enable Continue only once something is selected
    updateContinueState(root, choicesGrid) {
      const continueButton = this.findSlot(root, "continue");
      if (continueButton) continueButton.disabled = choicesGrid.querySelectorAll("input:checked").length === 0;
    }

    commitQuestion(step, choicesGrid) {
      const pickedValues = Array.from(choicesGrid.querySelectorAll("input:checked")).map((input) => input.value);
      if (!pickedValues.length) return;
      this.answers[step.id] = step.kind === "multi" ? pickedValues : pickedValues[0];
      this.goToStep(this.stepIndex + 1);
    }

    renderAdaptive(step) {
      let paragraphs, footer, variantImage, heading = step.heading;
      if (step.compose) {
        // body built from several parts, each keyed off a different answer
        paragraphs = step.compose.map((part) => {
          const answer = this.answers[part.basedOn];
          const key = Array.isArray(answer) ? answer[0] : answer;
          return (part.map && part.map[key]) || part.default || "";
        }).filter(Boolean);
        footer = step.footer;
      } else {
        const variant = this.pickVariant(step);
        paragraphs = Array.isArray(variant.body) ? variant.body : [variant.body || ""];
        footer = variant.footer || step.footer;
        variantImage = variant.image;
        heading = variant.heading || step.heading; // per-variant headline (Serolin), else the step's
      }
      const root = this.cloneTemplate("adaptive");
      this.fillProgressBar(root, this.progressPercent());
      this.setText(root, "heading", heading);

      const paragraphContainer = this.findSlot(root, "bodies");
      paragraphs.filter(Boolean).forEach((text) => {
        const paragraph = this.cloneTemplate("adaptive-body");
        paragraph.textContent = text;
        paragraphContainer.appendChild(paragraph);
      });
      this.setText(root, "footer", footer);

      const image = variantImage || step.image;
      if (image) this.setImage(root, "image", image, "");
      else { const media = this.findSlot(root, "media"); if (media) media.remove(); }

      const continueButton = this.setText(root, "continue", this.config.continueLabel || "Continue");
      if (continueButton) continueButton.addEventListener("click", () => this.goToStep(this.stepIndex + 1));
      this.wireBackButton(root);
      this.mount(root);
    }

    // first variant whose match[] intersects the answers it is based on
    pickVariant(step) {
      const chosenValues = new Set([].concat(this.answers[step.basedOn] || []));
      const match = (step.variants || []).find((variant) => (variant.match || []).some((value) => chosenValues.has(value)));
      return match || step.default || (step.variants || [])[0] || {};
    }

    wireBackButton(root) {
      const backButton = this.setText(root, "back", this.config.backLabel || "Back");
      if (backButton) backButton.addEventListener("click", () => this.goToStep(this.stepIndex - 1));
    }

    // --- contact (captured at the end) ---

    renderContact() {
      const contact = this.config.contact || {};
      const root = this.cloneTemplate("contact");
      this.fillProgressBar(root, 100);
      this.setText(root, "heading", contact.heading || "Almost there");
      this.setText(root, "body", contact.body);
      this.setText(root, "nameLabel", (contact.nameLabel || "First name") + "*");
      this.setText(root, "phoneLabel", (contact.phoneLabel || "Phone number") + "*");
      this.setText(root, "emailLabel", (contact.emailLabel || "Email address") + "*");

      const firstNameInput = this.findSlot(root, "first");
      const phoneInput = this.findSlot(root, "phone");
      const emailInput = this.findSlot(root, "email");
      firstNameInput.placeholder = contact.namePlaceholder || "Jane";
      phoneInput.placeholder = contact.phonePlaceholder || "403314522";
      emailInput.placeholder = contact.emailPlaceholder || "jane.smith@yourcompany.com";
      firstNameInput.value = this.firstName;
      phoneInput.value = this.phone;
      emailInput.value = this.email;

      this.setText(root, "submit", contact.button || "Claim my 15% off");
      const skipButton = this.setText(root, "skip", contact.skip || "No thanks, just show me my results.");
      if (skipButton) skipButton.addEventListener("click", () => {
        this.firstName = (firstNameInput.value || "").trim();
        this.finish();
      });

      this.findSlot(root, "form").addEventListener("submit", (event) => {
        event.preventDefault();
        if ((emailInput.value || "").indexOf("@") < 0) {
          emailInput.focus();
          if (emailInput.reportValidity) emailInput.reportValidity();
          return;
        }
        this.firstName = (firstNameInput.value || "").trim();
        this.phone = (phoneInput.value || "").trim();
        this.email = (emailInput.value || "").trim();
        this.finish();
      });

      this.setHtml(root, "disclaimer", contact.disclaimer ? this.linkify(contact.disclaimer, contact.privacyUrl) : "");
      this.mount(root);
      this.focusFirst();
    }

    // --- recommendation (3b rules) ---

    // evaluate one 3b condition against the answers (all / any / includesAny)
    matchCondition(condition) {
      if (!condition) return false;
      if (condition.all) return condition.all.every((c) => this.matchCondition(c));
      if (condition.any) return condition.any.some((c) => this.matchCondition(c));
      if (condition.answer && condition.includesAny) {
        const picked = new Set([].concat(this.answers[condition.answer] || []));
        return condition.includesAny.some((value) => picked.has(value));
      }
      return false;
    }

    // recommended product ids, ordered per 3a. No `recommendation` block (solo quizzes) → forced product.
    computeRecommendation() {
      const rec = this.config.recommendation;
      if (!rec) return this.config.forcedProductId ? [this.config.forcedProductId] : [];
      const order = rec.order || [];
      let matched = (rec.rules || []).filter((rule) => this.matchCondition(rule.when)).map((rule) => rule.product);
      if (!matched.length && rec.fallback) matched = [rec.fallback];
      const unique = matched.filter((id, i) => id && matched.indexOf(id) === i);
      return unique.sort((a, b) => {
        const ia = order.indexOf(a), ib = order.indexOf(b);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
      });
    }

    // --- submit + result ---

    finish() {
      // compute the 3b recommendation once — result screen and worker payload both read it
      this.recommended = this.computeRecommendation();
      const loadingScreen = this.cloneTemplate("loading");
      this.setText(loadingScreen, "text", this.config.loadingText || "Finding your match…");
      this.mount(loadingScreen);
      // race the call against a min spinner time (no flicker) and a hard cap (slow worker never blocks)
      const submitPromise = this.submitToWorker().catch(() => {});
      const minDelay = new Promise((resolve) => setTimeout(resolve, 700));
      const hardCap = new Promise((resolve) => setTimeout(resolve, 1600));
      Promise.race([Promise.all([submitPromise, minDelay]), hardCap]).then(() => this.renderResult());
    }

    submitToWorker() {
      if (!this.workerUrl) return Promise.resolve();
      const hasIdentifier = (this.email && this.email.indexOf("@") > -1) || this.phone;
      if (!hasIdentifier) return Promise.resolve(); // skipped contact → nothing to identify
      const questions = this.steps
        .filter((step) => step.type === "question")
        .map((step) => ({ id: step.id, title: step.title, type: step.kind, choices: step.choices }));
      const workerConfig = {
        quizId: this.config.quizId,
        forcedProductId: this.config.forcedProductId,
        products: this.config.products || [],
        questions,
      };
      return this.post("/api/submit", {
        name: this.firstName, email: this.email, phone: this.phone,
        quizId: this.config.quizId, answers: this.answers, config: workerConfig,
        // client-computed 3b recommendation (the worker trusts this for the combo event)
        recommendedProducts: this.recommended || [],
        recommendationType: (this.recommended || []).length > 1 ? "combo" : "solo",
        primaryRecommendation: (this.recommended || [])[0] || null,
      });
    }

    // router: one product → the rich solo screen; two or more → the card grid
    renderResult() {
      const result = this.config.result || {};
      if (result.plan) {
        const products = result.plan.products || {};
        const recommended = (this.recommended || []).filter((id) => products[id]);
        // a single match shows that product's own solo screen, merged over the shared result copy
        if (recommended.length === 1 && products[recommended[0]].solo) {
          return this.renderProductResult(Object.assign({}, result, products[recommended[0]].solo));
        }
        return this.renderPlan(result);
      }
      return this.renderProductResult(result);
    }

    renderProductResult(result) {
      const root = this.cloneTemplate("result");
      const ctaUrl = this.discountedUrl(result.ctaUrl);

      this.setText(root, "eyebrow", (this.firstName ? this.firstName.toUpperCase() + ", " : "") + (result.eyebrow || ""));
      this.setText(root, "heading", result.heading);
      if (result.image) this.setImage(root, "image", result.image, result.heading || "");
      this.setText(root, "badge", result.badge);
      this.setText(root, "productHeading", result.productHeading);
      const productIntro = this.setText(root, "productIntro", result.productIntro);
      if (productIntro && !result.productHeading) { // larger "lead" style when there's no product heading
        productIntro.classList.remove("text-[1.06rem]", "text-dark-blue");
        productIntro.classList.add("text-[clamp(1.25rem,1.9vw,1.6rem)]", "leading-[1.35]", "text-[#666f8b]");
      }

      const benefitsList = this.findSlot(root, "benefits");
      if (result.benefits && result.benefits.length) {
        result.benefits.forEach((benefit) => {
          const benefitEl = this.cloneTemplate("benefit");
          this.setText(benefitEl, "text", benefit);
          benefitsList.appendChild(benefitEl);
        });
      } else benefitsList.remove();

      this.setText(root, "productBody", result.productBody);
      this.setText(root, "legal", result.legal);
      const ctaLink = this.setText(root, "cta", result.cta || "Shop now");
      if (ctaLink) ctaLink.setAttribute("href", ctaUrl);
      this.setText(root, "trust", result.trust);

      const statsPlaceholder = this.findSlot(root, "stats");
      if (result.stats && result.stats.length) statsPlaceholder.replaceWith(this.buildStatsBlock(result, ctaUrl));
      else statsPlaceholder.remove();

      const cardsPlaceholder = this.findSlot(root, "cards");
      if (result.cards) cardsPlaceholder.replaceWith(this.buildCardsBlock(result.cards, ctaUrl));
      else cardsPlaceholder.remove();

      this.mount(root);
      this.dispatchEvent(new CustomEvent("quiz:completed", {
        bubbles: true, detail: { answers: this.answers, recommended: this.recommended },
      }));
    }

    // recommended ids in the order the cards are shown (plan.displayOrder), which is a presentation
    // choice and deliberately NOT the 3a priority order used for the Klaviyo event.
    planOrder(plan, products) {
      const order = plan.displayOrder || [];
      return (this.recommended || [])
        .filter((id) => products[id])
        .sort((a, b) => {
          const ia = order.indexOf(a), ib = order.indexOf(b);
          return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
        });
    }

    // multi-product result (result.plan): one card per recommended product + a subscription promo
    renderPlan(result) {
      const plan = result.plan || {};
      const products = plan.products || {};
      const recommended = this.planOrder(plan, products);
      const root = this.cloneTemplate("plan");

      this.setText(root, "eyebrow", (this.firstName ? this.firstName.toUpperCase() + ", " : "") + (result.eyebrow || ""));
      // heading names exactly what matched, so it can never disagree with the cards below
      const names = recommended.map((id) => products[id].name).filter(Boolean);
      this.setText(root, "heading", names.length ? (plan.headingPrefix || "") + names.join(", ") : result.heading);
      // intro is per-count — "Both products…" only reads correctly for two
      const intros = plan.intros || {};
      this.setText(root, "intro", intros[String(recommended.length)] || result.intro);

      const tilesGrid = this.findSlot(root, "tiles");
      tilesGrid.classList.add(recommended.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-2");
      recommended.forEach((id) => tilesGrid.appendChild(this.buildPlanCard(products[id], plan, id)));

      const promoPlaceholder = this.findSlot(root, "promo");
      if (plan.promo) promoPlaceholder.replaceWith(this.buildPlanPromo(plan.promo));
      else promoPlaceholder.remove();

      this.setText(root, "trust", result.trust);
      this.mount(root);
      this.dispatchEvent(new CustomEvent("quiz:completed", {
        bubbles: true, detail: { answers: this.answers, recommended: this.recommended },
      }));
    }

    buildPlanCard(product, plan, id) {
      const root = this.cloneTemplate("plan-card");
      const live = (this.live || {})[id] || {};
      this.fillRating(root, product, live);

      // a live product tag listed in plan.badgeTags wins over the badge hard-coded in the JSON
      let badgeText = product.badge, badgeStyle = product.badgeStyle;
      const badgeTags = plan.badgeTags || {};
      (live.tags || []).forEach((tag) => {
        if (badgeTags[tag]) { badgeText = tag; badgeStyle = badgeTags[tag]; }
      });

      const badgePlaceholder = this.findSlot(root, "badge");
      if (badgeText) {
        const badge = this.cloneTemplate("plan-badge-" + (badgeStyle === "new" ? "new" : "popular"));
        badge.textContent = badgeText; // the cloned <span> IS the badge, so set it directly
        badgePlaceholder.replaceWith(badge);
      } else badgePlaceholder.remove();

      this.setText(root, "name", product.name);
      this.setText(root, "tagline", product.tagline);
      if (product.image) this.setImage(root, "image", product.image, product.name || "");
      else { const media = this.findSlot(root, "media"); if (media) media.remove(); }
      this.setText(root, "claim", product.claim);
      this.setText(root, "footnote", product.footnote);

      const cta = this.setText(root, "cta", product.cta || plan.cta || "Learn more");
      // product CTAs go straight to the PDP — the discount is offered once, in the promo strip
      if (cta) cta.setAttribute("href", product.ctaUrl || "#");
      return root;
    }

    // stars + review count from live Okendo data, falling back to whatever the JSON declares.
    // A product with no reviews yet loses the whole row rather than showing "0 Reviews".
    fillRating(root, product, live) {
      const count = live.ratingCount != null ? live.ratingCount : product.ratingCount;
      const rating = live.rating != null ? live.rating : product.rating;
      const label = count > 0 ? count + (count === 1 ? " Review" : " Reviews") : product.reviews;
      if (!label) { const row = this.findSlot(root, "rating"); if (row) row.remove(); return; }

      const stars = this.findSlot(root, "stars");
      const percent = rating ? Math.round(Math.max(0, Math.min(100, (rating / 5) * 100)) * 10) / 10 : 100;
      if (stars) stars.style.width = percent + "%";
      this.setText(root, "reviews", label);
    }

    buildPlanPromo(promo) {
      const root = this.cloneTemplate("plan-promo");
      this.setImage(root, "image", promo.image, "");
      this.setText(root, "heading", promo.heading);
      this.setText(root, "body", promo.body);
      const cta = this.setText(root, "cta", promo.cta || "Shop all products");
      if (cta) cta.setAttribute("href", this.discountedUrl(promo.ctaUrl));
      return root;
    }

    // clinical-stats block (figures + footnotes)
    buildStatsBlock(result, ctaUrl) {
      const root = this.cloneTemplate("stats");
      this.setText(root, "eyebrow", result.statsEyebrow);
      if (result.statsSans) { // swap the serif heading for the sans variant
        const sansHeading = this.cloneTemplate("stats-h-sans");
        this.findSlot(root, "heading").replaceWith(sansHeading);
        this.setText(root, "heading", result.statsHeading);
      } else {
        this.setText(root, "heading", result.statsHeading);
      }

      const statsGrid = this.findSlot(root, "grid");
      result.stats.forEach((stat) => {
        const statEl = this.cloneTemplate("stat");
        this.setText(statEl, "figure", stat.figure);
        this.setText(statEl, "label", stat.label);
        statsGrid.appendChild(statEl);
      });
      const ctaLink = this.setText(root, "cta", result.cta || "Shop now");
      if (ctaLink) ctaLink.setAttribute("href", ctaUrl);

      const footnotesContainer = this.findSlot(root, "footnotes");
      if (result.footnotes && result.footnotes.length) {
        result.footnotes.forEach((footnote) => {
          const footnoteEl = this.cloneTemplate("footnote");
          footnoteEl.textContent = footnote;
          footnotesContainer.appendChild(footnoteEl);
        });
      } else footnotesContainer.remove();
      return root;
    }

    // secondary result block: life-stage / benefit cards (+ a promo card)
    buildCardsBlock(cards, ctaUrl) {
      const root = this.cloneTemplate("cards");
      this.setText(root, "heading", cards.heading);
      this.setText(root, "intro", cards.intro);
      const ctaLink = this.setText(root, "cta", cards.cta);
      if (ctaLink) ctaLink.setAttribute("href", cards.ctaUrl ? this.discountedUrl(cards.ctaUrl) : ctaUrl);

      const cardsGrid = this.findSlot(root, "grid");
      (cards.items || []).forEach((item) => {
        const cardEl = this.cloneTemplate("card");
        this.setText(cardEl, "title", item.title);
        this.setText(cardEl, "desc", item.desc);
        const tagsContainer = this.findSlot(cardEl, "tags");
        if (item.tags && item.tags.length) {
          item.tags.forEach((tag) => {
            const tagEl = this.cloneTemplate("tag");
            this.setText(tagEl, "text", tag);
            tagsContainer.appendChild(tagEl);
          });
        } else tagsContainer.remove();
        cardsGrid.appendChild(cardEl);
      });

      if (cards.promo) {
        const promo = cards.promo;
        const promoEl = this.cloneTemplate("promo"); // the cloned <a> IS the link
        promoEl.setAttribute("href", promo.url || "#");
        if (promo.image) {
          promoEl.style.backgroundImage =
            "linear-gradient(180deg,rgba(6,12,36,.2),rgba(6,12,36,.78)),url('" + this.optimizeSrc(promo.image) + "')";
        }
        this.setText(promoEl, "label", promo.label);
        this.setText(promoEl, "title", promo.title);
        this.setText(promoEl, "cta", promo.cta);
        cardsGrid.appendChild(promoEl);
      }
      return root;
    }

    // --- small utilities ---

    // route Shop CTAs through the discount link so the coupon applies automatically
    discountedUrl(productUrl) {
      const discountUrl = this.config.discountUrl;
      if (discountUrl && productUrl) {
        return discountUrl + (discountUrl.indexOf("?") > -1 ? "&" : "?") + "redirect=" + encodeURIComponent(productUrl);
      }
      return discountUrl || productUrl || "#";
    }

    // when the copy ends with a "View (our) Privacy Policy" call-out, only that one is linked —
    // the earlier in-sentence mention stays plain text. Otherwise every mention is linked.
    linkify(text, url) {
      if (!url) return this.escapeHtml(text);
      const safe = this.escapeHtml(text);
      const anchor = '<a href="' + this.escapeHtml(url) + '">Privacy Policy</a>';
      const callout = safe.replace(/(View (?:our )?)Privacy Policy/, (match, view) => view + anchor);
      if (callout !== safe) return callout;
      return safe.replace(/Privacy Policy/g, () => anchor);
    }

    escapeHtml(text) {
      const container = document.createElement("div");
      container.textContent = text || "";
      return container.innerHTML;
    }

    focusFirst() {
      const focusable = this.stage.querySelector("input, button, a");
      if (focusable) focusable.focus({ preventScroll: true });
    }

    post(path, body) {
      return fetch(this.workerUrl + path, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      }).then((response) => response.json());
    }

    // --- static: find + register ---

    // resolve a quiz by id (#quiz-<id> or #<id>), else the first on the page
    static find(id) {
      if (id) return document.getElementById("quiz-" + id) || document.getElementById(id);
      return document.querySelector("biolae-quiz");
    }

    // open the quiz a trigger points at; returns true if one was opened
    static openFromTrigger(trigger) {
      const id = trigger.getAttribute("data-quiz-open") || (trigger.getAttribute("href") || "").replace(/^#quiz-?/, "");
      const quiz = BiolaeQuiz.find(id || "");
      if (quiz && quiz.open) { quiz.open(); return true; }
      return false;
    }

    // define the element, wire global triggers, expose window.BiolaeQuiz
    static register() {
      if (customElements.get("biolae-quiz")) return;
      customElements.define("biolae-quiz", BiolaeQuiz);

      document.addEventListener("click", (event) => {
        const trigger = event.target.closest("[data-quiz-open], a[href^='#quiz']");
        if (trigger && BiolaeQuiz.openFromTrigger(trigger)) event.preventDefault();
      });

      window.BiolaeQuiz = {
        open: (id) => { const quiz = BiolaeQuiz.find(id); if (quiz) quiz.open(); },
        close: (id) => { const quiz = BiolaeQuiz.find(id); if (quiz) quiz.close(); },
      };
    }
  }

  BiolaeQuiz.register();
})();
